import classNames from 'classnames';
import Link from 'next/link';
import { getCommunityBannerUrl, getCommunityLogoUrl } from 'src/utils/linkUtils';
import { useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { Element, Section } from 'src/constants/footfall';
import FollowButton from 'src/components/buttons/followButton';
import CommunityLink from 'src/modules/shared/links/communityLink';
import { userIsMemberOfCurrentCommunity } from 'src/utils/context';
import { Community } from 'src/constants/types';
import Icon from 'src/components/icon';

type Props = {
  community: Community;
  onCommunityClick?: (communityId: number) => void;
  onFollowCommunity?: (community: Community) => void;
};
export default function SearchCommunityCard({
  community,
  onCommunityClick,
  onFollowCommunity,
}: Props) {
  const currentUser = useSelector(({ context }) => context.currentUser);
  const {
    bannerImageHash,
    communityId,
    description,
    logoImageHash,
    name,
    slug,
    totalMembers,
  } = community;

  const isMemberOfCommunity = userIsMemberOfCurrentCommunity(currentUser, community);

  const memberCount = Number(totalMembers).toLocaleString() + (totalMembers === 1 ? i18n.t(' member') : i18n.t(' members'));

  const headerStyle = {
    backgroundImage: `url(${getCommunityBannerUrl(bannerImageHash)})`,
  };

  const logoStyle = {
    backgroundImage: `url(${getCommunityLogoUrl(logoImageHash)})`,
  };

  const MULTILINE_TITLE_LIMIT = 29;

  const titleClassName = classNames({
    'base-card__title': true,
    'base-card__title--multiline': name.length > MULTILINE_TITLE_LIMIT,
  });

  return (
    <div className="base-card" data-testid="base-card">
      <div className="base-card__inner community-card__inner">
        <CommunityLink
          name={name}
          slug={slug}
          onClick={() => onCommunityClick(communityId)}
        >
          <div className="community-card__banner">
            <div
              className="base-card__header"
              style={headerStyle}
            />
            <div className="community-card__logo">
              <div
                className="community-card__logo-img"
                style={logoStyle}
              />
            </div>
          </div>
        </CommunityLink>
        <div className="base-card__content community-card__content">
          <h3 className={titleClassName}>
            <CommunityLink
              name={name}
              slug={slug}
              onClick={() => onCommunityClick(communityId)}
            >
              {name}
            </CommunityLink>
          </h3>
          <div className="base-card__subtitle">
            <CommunityLink
              name={name}
              slug={slug}
              onClick={() => onCommunityClick(communityId)}
            >
              {memberCount}
            </CommunityLink>
          </div>
          <div className="base-card__body">
            <Link
              as={`/${slug}/about`}
              href={`/community/communityAbout?communitySlug=${slug}`}
              onClick={() => onCommunityClick(communityId)}
            >
              {description}
            </Link>
          </div>
          <div className="base-card__footer community-card__footer">
            {
              !!description && (
                <Link
                  as={`/${slug}/about`}
                  className="base-card__cta community-card__cta"
                  href={`/community/communityAbout?communitySlug=${slug}`}
                  onClick={() => onCommunityClick(communityId)}
                >
                  {i18n.t('Read more')} <Icon icon="open-right" />
                </Link>
              )}
            <div className="community-card__follow-button">
              <FollowButton
                buttonType="secondary"
                community={community}
                followType="community"
                fullWidth={true}
                isFollowing={isMemberOfCommunity}
                noIcon={false}
                signupContext={{
                  element: Element.FOLLOW,
                  section: Section.SEARCH_COMMUNITY_CARD,
                }}
                onSignupSuccess={() => onFollowCommunity(community)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
